import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Autocomplete,
  TextField,
  createFilterOptions,
  InputAdornment,
} from "@mui/material";
import Box from "@mui/material/Box";
import ClipLoader from "react-spinners/ClipLoader";
import { Image } from "hesus-ui-kit";
import { useDebounce } from "use-debounce";
import searchIcon from "@icons/ic-search-blue.svg";
import {
  autoSearchInputStyles,
  autoSearchInputStylesError,
  RequiredMessage,
} from "@utils/extra-style";
import { ContainerLabel } from "../../AddEditPurchaseOrder.styled";

const SUPPLIER_OBJECT_TYPES = ["supplier", "center", "haulier", "company"];

const PurchaseOrderSupplierSelect = ({
  t,
  value,
  onChange,
  countryId,
  companyId,
  companyName,
  error,
  disabled,
}) => {
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierSearchQuery, setSupplierSearchQuery] = useState("");
  const [debouncedSupplierSearchQuery] = useDebounce(supplierSearchQuery, 700);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  const enrichedSupplierOptions = useMemo(() => {
    if (!companyId || !companyName) {
      return supplierOptions;
    }
    const alreadyPresent = supplierOptions.some((s) => s.id === companyId);
    if (alreadyPresent) {
      return supplierOptions;
    }
    return [{ id: companyId, name: companyName }, ...supplierOptions];
  }, [supplierOptions, companyId, companyName]);

  const fetchSuppliers = (search = "") => {
    if (!countryId) return;
    setLoadingSuppliers(true);
    const searchParam = search
      ? `&search=${encodeURIComponent(search)}`
      : companyId
      ? `&objectId=${companyId}`
      : "";
    axios
      .get(
        `/general/search?countries=[${countryId}]&limit=50&objectTypes=${JSON.stringify(
          SUPPLIER_OBJECT_TYPES
        )}${searchParam}&orderColumn=name&orderBy=ASC`
      )
      .then((response) => {
        setSupplierOptions(response.data?.companies || []);
      })
      .catch(() => setSupplierOptions([]))
      .finally(() => setLoadingSuppliers(false));
  };

  useEffect(() => {
    fetchSuppliers(debouncedSupplierSearchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSupplierSearchQuery, countryId]);

  const supplierSyncedRef = useRef(false);

  useEffect(() => {
    if (supplierSyncedRef.current) return;
    if (!companyId || supplierOptions.length === 0) return;
    const fullOption = supplierOptions.find((s) => s.id === companyId);
    if (!fullOption) return;
    supplierSyncedRef.current = true;
    onChange(null, fullOption);
    
  }, [supplierOptions, companyId]);

  return (
    <div>
      <ContainerLabel required>
        {t("SUPPLIER")}
        {loadingSuppliers && (
          <ClipLoader size={13} style={{ marginLeft: "8px" }} />
        )}
      </ContainerLabel>
      <div style={{ minHeight: "58px" }}>
        <Autocomplete
          id="po-supplier"
          fullWidth
          options={enrichedSupplierOptions}
          getOptionLabel={(option) => option.name || ""}
          filterSelectedOptions
          disabled={disabled}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id}>
              {option.name}
            </Box>
          )}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === "input") {
              setSupplierSearchQuery(newInputValue || "");
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("SUPPLIER")}
              size="small"
              style={
                error !== "" ? autoSearchInputStylesError : autoSearchInputStyles
              }
              InputProps={{
                ...params.InputProps,
                style: { height: "40px" },
                endAdornment: (
                  <InputAdornment position="end">
                    <Image src={searchIcon} alt="search" />
                  </InputAdornment>
                ),
              }}
            />
          )}
          value={value || null}
          onChange={onChange}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          filterOptions={(options, params) => {
            const filtered = createFilterOptions()(options, params);
            return [...filtered];
          }}
        />
        {error && <RequiredMessage>{error}</RequiredMessage>}
      </div>
    </div>
  );
};

PurchaseOrderSupplierSelect.propTypes = {
  t: PropTypes.func.isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  countryId: PropTypes.number,
  companyId: PropTypes.number,
  companyName: PropTypes.string,
  error: PropTypes.string,
  disabled: PropTypes.bool,
};

PurchaseOrderSupplierSelect.defaultProps = {
  value: null,
  countryId: null,
  companyId: null,
  companyName: "",
  error: "",
  disabled: false,
};

export default PurchaseOrderSupplierSelect;
